/* ============================================
   CART — Shopify AJAX Cart API integration
   ============================================ */

(function() {
  'use strict';

  window.Brume = window.Brume || {};

  /* ── Scroll lock ──────────────────────────────
     `body { overflow: hidden }` is the usual way to stop the page scrolling
     behind an overlay, and iOS Safari ignores it — the page keeps moving under
     your finger and you land somewhere else when the overlay closes. Pinning the
     body with `position: fixed` at a negative offset is the technique iOS does
     honour; the offset is restored on unlock so the page doesn't jump.

     Deliberately a boolean, not a reference count: the mobile menu hands off to
     the cart drawer without unlocking in between, so a counter would end up
     above zero with nothing open and leave the page permanently frozen. A
     boolean's worst case is an overlay that scrolls — the bug we already had —
     rather than a page nobody can move. */
  var scrollLocked = false;
  var scrollLockY = 0;

  function lockScroll() {
    if (scrollLocked) return; // keep the first saved offset; body is fixed so scrollY reads 0 now
    scrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var b = document.body;
    b.style.position = 'fixed';
    b.style.top = -scrollLockY + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.style.overflow = 'hidden';
    scrollLocked = true;
  }

  /* Every overlay that takes the lock. Each one drops its is-open class before
     calling unlockScroll, so anything still marked open is still on screen,
     and the page must stay pinned under it: the email popup can land on top of
     an open cart drawer, and closing the popup used to free the page while the
     drawer was still showing. Read from the page rather than counted, so the
     menu handing straight over to the drawer (which closes the menu without
     unlocking) cannot leave the page frozen. */
  var LOCKING_OVERLAYS = '#cartDrawer.is-open, #brumePopup.is-open, #rvModal.is-open, #nav.is-open';

  function unlockScroll() {
    if (!scrollLocked) return;
    if (document.querySelector(LOCKING_OVERLAYS)) return;
    var b = document.body;
    b.style.position = '';
    b.style.top = '';
    b.style.left = '';
    b.style.right = '';
    b.style.width = '';
    b.style.overflow = '';
    scrollLocked = false;
    /* html carries scroll-behavior:smooth, which turns this restore into an
       animation — and removing position:fixed in the same frame cancels it, so
       the page lands at the top instead of where the drawer was opened. An
       inline 'auto' outranks the stylesheet for this one call; the previous
       value goes back so the rest of the page keeps scrolling smoothly. */
    var root = document.documentElement;
    var prevBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, scrollLockY);
    root.style.scrollBehavior = prevBehavior;
  }

  window.Brume.lockScroll = lockScroll;
  window.Brume.unlockScroll = unlockScroll;

  var listeners = [];
  var cachedCart = null;

  /* ── Reads yield to writes ────────────────────
     A /cart.js read sent before an add, and answering after it, carries the
     cart as it was before the add. Assigning it unconditionally emptied the bag
     a few seconds after a tap while Shopify still held the item: reproduced on
     live with a slow first read, the drawer went to 0 and stayed there.

     So a whole-cart answer only replaces the cache when nothing newer can have
     happened since it was asked for:
       - a read counts only if no write was in flight when it was sent and none
         started before it answered;
       - a write's own full-cart answer counts only if no later write started.
     Writes are never held back to make this work: they go out at once, so the
     checkout guard's 3s wait is never spent waiting on a read. Anything turned
     away is made good by one read sent once every write has finished. */
  var writeSeq = 0;         // writes started, ever
  var writesInFlight = 0;
  var overlapped = false;   // two writes were in flight together since the last trusted read
  var appliedWaiters = [];  // resolved the next time a whole cart is applied

  function beginWrite() {
    if (writesInFlight > 0) overlapped = true;
    writesInFlight++;
    var seq = ++writeSeq, open = true;
    return {
      seq: seq,
      // Idempotent: a success handler that throws lands in catch, which ends it again.
      end: function () { if (open) { open = false; writesInFlight--; } }
    };
  }

  /* Ends a write; once the last one is done, fetches the truth if anything
     could have been turned away on the way (always, after an add, for the
     cart-level discounts only the server works out). */
  function afterWrite(w, alwaysReconcile) {
    w.end();
    if (writesInFlight === 0 && (alwaysReconcile || overlapped)) fetchCart();
  }

  function isCart(c) {
    return !!(c && Array.isArray(c.items));
  }

  /* Replace the cache with a whole cart from Shopify. A provisional line whose
     add is still in flight is kept, unless the incoming cart already has that
     product (Shopify handled the add first), or it would vanish and reappear. */
  function applyCart(cart) {
    var carry = [];
    if (cachedCart && cachedCart.items) {
      for (var i = 0; i < cachedCart.items.length; i++) {
        var line = cachedCart.items[i];
        if (String(line.key).indexOf('pending-') !== 0) continue;
        var id = identityOf(line), present = false;
        for (var j = 0; j < cart.items.length; j++) {
          if (identityOf(cart.items[j]) === id) { present = true; break; }
        }
        if (!present) carry.push(line);
      }
    }
    cachedCart = cart;
    if (carry.length) {
      cachedCart.items = cachedCart.items.concat(carry);
      recomputeTotals();
    }
    var waiting = appliedWaiters;
    appliedWaiters = [];
    notifyListeners();
    for (var w = 0; w < waiting.length; w++) waiting[w]();
  }

  /* For a write whose own answer was turned away: settle when the cart that
     replaces it lands, so the drawer only drops its tapped-quantity override
     once there is a true number to show. Capped, so a lost request can never
     hold a caller (and its disabled buttons) forever. */
  function nextApplied() {
    return new Promise(function (resolve) {
      var done = false;
      function finish() { if (!done) { done = true; resolve(cachedCart); } }
      appliedWaiters.push(finish);
      setTimeout(finish, 4000);
    });
  }

  // ── Fetch ────────────────────────────────────

  /* Fold what /cart/add.js returned into the cached cart instead of asking
     Shopify for the whole cart again. The add response already describes the
     line it created or topped up, and that second request cost about 250ms of
     dead time before the drawer could show anything. Totals are recomputed
     from the lines, then a background refresh picks up what only the server
     knows (cart-level discounts, its own item_count). */
  function recomputeTotals() {
    var count = 0, total = 0;
    for (var i = 0; i < cachedCart.items.length; i++) {
      count += cachedCart.items[i].quantity || 0;
      total += cachedCart.items[i].final_line_price || 0;
    }
    cachedCart.item_count = count;
    cachedCart.total_price = total;
  }

  function mergeAddedItems(data) {
    var added = (data && data.items) ? data.items : (data && data.key ? [data] : []);
    if (!added.length) return cachedCart;   // nothing to fold in; afterWrite's read brings the truth
    /* No cache yet: on a slow phone the page-load read had not answered when
       they added, and this add has turned it away. Waiting for the next read
       instead left the drawer drawing "Your bag is empty" once the caller
       dropped its spinner, so start the cache from what the add returned; the
       read afterWrite has just sent replaces it with the whole cart, lines
       from an earlier visit included. */
    if (!cachedCart || !cachedCart.items) cachedCart = { items: [] };
    for (var a = 0; a < added.length; a++) {
      var line = added[a], found = false;
      for (var i = 0; i < cachedCart.items.length; i++) {
        if (cachedCart.items[i].key === line.key) { cachedCart.items[i] = line; found = true; break; }
      }
      if (!found) cachedCart.items.push(line);
    }
    recomputeTotals();
    notifyListeners();
    return cachedCart;    // the reconcile is afterWrite's, sent as the add ended
  }

  function fetchCart() {
    var seqAtStart = writeSeq;
    var sentClean = writesInFlight === 0;
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    })
    .then(function(response) { return response.json(); })
    .then(function(cart) {
      /* Stale: a write overlapped this read, so it may predate it. The write
         that turned it away sends its own read once everything has finished. */
      if (!sentClean || seqAtStart !== writeSeq || !isCart(cart)) return cachedCart;
      overlapped = false;
      applyCart(cart);
      return cart;
    })
    .catch(function(err) {
      console.error('Brume Cart: Failed to fetch cart', err);
      return cachedCart;
    });
  }

  // ── Read ────────────────────────────────────

  function getCart() {
    if (!cachedCart) return [];
    return cachedCart.items || [];
  }

  function getCartCount() {
    if (!cachedCart) return 0;
    return cachedCart.item_count || 0;
  }

  function getCartTotal() {
    if (!cachedCart) return 0;
    return (cachedCart.total_price || 0) / 100;
  }

  // ── Write ───────────────────────────────────

  /* A provisional line, drawn before Shopify answers.
     /cart/add.js takes about 600ms, and until it returned the drawer showed a
     spinner, so the bag looked empty right after a tap. The caller passes what
     it already knows (title, variant, price in cents) and the line appears at
     once, carrying _pending so the drawer leaves its controls out; the real
     line from Shopify replaces it, or it is removed if the add fails. */
  var pendingAddSeq = 0;

  function identityOf(o) {
    var bundle = (o && o.properties && o.properties._bundle) || '';
    return (o && (o.handle || '')) + '|' + (o && (o.variant_title || '')) + '|' + bundle;
  }

  function addPendingLine(preview, quantity) {
    if (!preview || !cachedCart || !cachedCart.items) return null;
    var qty = quantity || 1;

    /* Same product and colour already in the bag: raise that line instead of
       showing a second, temporary one. The server will return the same line
       with the same total, so the drawer never has to rebuild its list. */
    for (var e = 0; e < cachedCart.items.length; e++) {
      var line = cachedCart.items[e];
      if (!line._pending && identityOf(line) === identityOf(preview)) {
        var unit = line.quantity ? Math.round(line.final_line_price / line.quantity) : (preview.price || 0);
        line.quantity += qty;
        line.final_line_price = unit * line.quantity;
        line.original_line_price = (line.original_line_price || line.final_line_price);
        line._pending = true;
        recomputeTotals();
        notifyListeners();
        return line.key;
      }
    }

    var key = 'pending-' + (++pendingAddSeq);
    var cents = (preview.price || 0) * qty;
    cachedCart.items.push({
      key: key,
      quantity: qty,
      product_title: preview.title || '',
      variant_title: preview.variant_title || '',
      handle: preview.handle || '',
      image: preview.image || null,
      properties: preview.properties || {},
      final_line_price: cents,
      original_line_price: cents,
      _pending: true
    });
    recomputeTotals();
    notifyListeners();
    return key;
  }

  /* Confirmed: just clear the flag, the server's line replaces it next.
     Failed: a provisional line goes, a raised line is left to the refresh. */
  function dropPendingLine(key, failed) {
    if (!key || !cachedCart || !cachedCart.items) return;
    for (var i = 0; i < cachedCart.items.length; i++) {
      if (cachedCart.items[i].key !== key) continue;
      if (String(key).indexOf('pending-') === 0) {
        /* Always: on success the server's own line takes its place, and
           leaving it would briefly show the item twice and force the drawer
           to rebuild, reloading the photo. */
        cachedCart.items.splice(i, 1);
      } else {
        delete cachedCart.items[i]._pending;
        if (failed) fetchCart();
      }
      break;
    }
    recomputeTotals();
  }

  function addToCart(variantId, quantity, sellingPlan, preview) {
    var item = { id: variantId, quantity: quantity || 1 };
    if (sellingPlan) {
      item.selling_plan = sellingPlan;
    }
    var pendingKey = addPendingLine(preview, quantity);
    var w = beginWrite();
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [item]
      })
    })
    .then(function(response) {
      return response.json().then(function(data) {
        if (!response.ok) {
          // Shopify returns 422 with a description (e.g. "...is already sold out.")
          var err = new Error(data.description || data.message || 'Could not add item to bag');
          err.cartError = data;
          throw err;
        }
        return data;
      });
    })
    .then(function(data) {
      afterWrite(w, true);
      dropPendingLine(pendingKey);
      return mergeAddedItems(data);
    })
    .catch(function(err) {
      afterWrite(w, false);
      dropPendingLine(pendingKey, true);
      notifyListeners();
      throw err;
    });
  }

  // Add several line items in one request (e.g. filter + refill subscription).
  /* Several lines in one request (the two-colour bundle), each drawn at once
     from its preview like addToCart. Laid down in the order listed: every
     provisional line redraws the drawer, which gives each new line the next
     place in its added-order sort, so the first listed shows first. */
  function addItems(items, previews) {
    var pendingKeys = [];
    if (previews && previews.length) {
      for (var pv = 0; pv < previews.length; pv++) {
        var k = addPendingLine(previews[pv], (items[pv] && items[pv].quantity) || 1);
        if (k) pendingKeys.push(k);
      }
    }
    var w = beginWrite();
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
    .then(function(response) {
      return response.json().then(function(data) {
        if (!response.ok) {
          var err = new Error(data.description || data.message || 'Could not add items to bag');
          err.cartError = data;
          throw err;
        }
        return data;
      });
    })
    .then(function(data) {
      afterWrite(w, true);
      for (var d = 0; d < pendingKeys.length; d++) dropPendingLine(pendingKeys[d]);
      return mergeAddedItems(data);
    })
    .catch(function(err) {
      afterWrite(w, false);
      for (var f = 0; f < pendingKeys.length; f++) dropPendingLine(pendingKeys[f], true);
      notifyListeners();
      throw err;
    });
  }

  /* /cart/change.js, update.js and clear.js answer with the whole cart. That
     answer is applied only if no later write has started; otherwise it may
     predate that write, and the read sent once all writes finish replaces it.
     A body that is not a cart is never applied: it goes to the caller's catch,
     which takes the truth back instead. */
  function applyWriteAnswer(w, cart) {
    if (!isCart(cart)) throw new Error('Unexpected cart response');
    var newest = w.seq === writeSeq;
    afterWrite(w, false);
    if (!newest) return nextApplied();
    applyCart(cart);
    return cart;
  }

  /* A full-cart write that failed: the drawer has already moved (a step, or a
     line binned), so take the truth back from the server rather than leave the
     bag showing something the checkout will not agree with. */
  function recoverFromWrite(w) {
    w.end();
    fetchCart();
    return nextApplied();
  }

  function updateQuantity(lineItemKey, newQty) {
    var w = beginWrite();
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineItemKey,
        quantity: newQty
      })
    })
    .then(function(response) { return response.json(); })
    .then(function(cart) { return applyWriteAnswer(w, cart); })
    .catch(function(err) {
      console.error('Brume Cart: Failed to update quantity', err);
      return recoverFromWrite(w);
    });
  }

  /* Several lines in one request. A bundle's lines have to change together,
     and two separate /cart/change.js calls can answer in either order, leaving
     the older cart on screen; /cart/update.js takes them all and gives one
     answer to trust. Keys are line item keys. */
  function updateQuantities(updates) {
    var w = beginWrite();
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
    .then(function(response) { return response.json(); })
    .then(function(cart) { return applyWriteAnswer(w, cart); })
    .catch(function(err) {
      console.error('Brume Cart: Failed to update quantities', err);
      return recoverFromWrite(w);
    });
  }

  // Remove several lines at once (the bundle's bin), drawn at once like removeItem.
  function removeItems(keys) {
    if (cachedCart && cachedCart.items) {
      cachedCart.items = cachedCart.items.filter(function(l) { return keys.indexOf(l.key) === -1; });
      recomputeTotals();
      notifyListeners();
    }
    var updates = {};
    for (var i = 0; i < keys.length; i++) updates[keys[i]] = 0;
    return updateQuantities(updates);
  }

  function removeItem(lineItemKey) {
    /* Drop the line locally first so the drawer reacts to the tap straight
       away (and closes if that was the last item), then tell Shopify. The
       refresh that follows the update puts it back if the call failed. */
    if (cachedCart && cachedCart.items) {
      for (var i = 0; i < cachedCart.items.length; i++) {
        if (cachedCart.items[i].key === lineItemKey) {
          cachedCart.items.splice(i, 1);
          recomputeTotals();
          notifyListeners();
          break;
        }
      }
    }
    return updateQuantity(lineItemKey, 0);
  }

  function clearCart() {
    var w = beginWrite();
    return fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    })
    .then(function(response) { return response.json(); })
    .then(function(cart) { return applyWriteAnswer(w, cart); })
    .catch(function(err) {
      console.error('Brume Cart: Failed to clear cart', err);
      return recoverFromWrite(w);
    });
  }

  // ── Events ──────────────────────────────────

  function onCartChange(callback) {
    listeners.push(callback);
  }

  function notifyListeners() {
    listeners.forEach(function(cb) { cb(); });
  }

  // ── Init ────────────────────────────────────

  fetchCart();

  // ── Expose API ──────────────────────────────

  window.Brume.Cart = {
    fetchCart: fetchCart,
    getCart: getCart,
    getCartCount: getCartCount,
    getCartTotal: getCartTotal,
    addToCart: addToCart,
    addItems: addItems,
    updateQuantity: updateQuantity,
    removeItem: removeItem,
    updateQuantities: updateQuantities,
    removeItems: removeItems,
    clearCart: clearCart,
    onCartChange: onCartChange
  };

})();
